import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Header.css';
import { API_BASE } from '../config/api';
import { FaInfoCircle, FaDollarSign } from 'react-icons/fa';

function Header({ onSearch, books = [] }) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const normalizedQuery = query.trim().toLowerCase();

  const suggestions = useMemo(() => {
    if (!normalizedQuery) return { books: [], authors: [] };

    const bookMatches = books
      .filter((b) => (b.title || '').toLowerCase().includes(normalizedQuery))
      .slice(0, 6)
      .map((b) => ({
        id: b.id,
        title: b.title || 'Livre',
        cover: b.cover || 'uploads/covers/default-cover.png'
      }));

    const authorMap = new Map();
    books.forEach((b) => {
      const authorName = b.publisher_name || b.author;
      if (!authorName) return;
      const normalizedAuthor = String(authorName).toLowerCase();
      if (!normalizedAuthor.includes(normalizedQuery)) return;
      if (authorMap.has(normalizedAuthor)) return;

      authorMap.set(normalizedAuthor, {
        name: authorName,
        authorId: b.author_id
      });
    });

    return {
      books: bookMatches,
      authors: Array.from(authorMap.values()).slice(0, 6)
    };
  }, [books, normalizedQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    const value = query.trim();
    if (onSearch) onSearch(value);
    setShowSuggestions(false);
    if (value) navigate('/');
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (onSearch) onSearch(value.trim());
    setShowSuggestions(true);
  };

  const handleBookSuggestionClick = (bookId, title) => {
    setQuery(title || '');
    setShowSuggestions(false);
    if (onSearch) onSearch(title || '');
    navigate(`/book/${bookId}`);
  };

  const handleAuthorSuggestionClick = (authorName, authorId) => {
    setQuery(authorName || '');
    setShowSuggestions(false);
    if (onSearch) onSearch(authorName || '');
    navigate(authorId ? `/creator/${authorId}` : '/');
  };

  const handleAboutClick = () => navigate('/about');
  const handleMonetizationClick = () => navigate('/monetization');

  return (
    <header className="header">
      <button type="button" className="brand" onClick={() => navigate('/')} aria-label="Accueil BookTube">
        <img src="/logo-booktube.png" alt="BookTube" className="brand-logo" />
        <span className="logo">BookTube</span>
      </button>

      <form className="search-form" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="Rechercher un livre ou auteur..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
        />
        <button type="submit">Rechercher</button>

        {showSuggestions && normalizedQuery && (suggestions.books.length > 0 || suggestions.authors.length > 0) && (
          <div className="search-suggestions">
            {suggestions.books.length > 0 && (
              <div className="suggestion-group">
                <div className="suggestion-title">Livres</div>
                {suggestions.books.map((book) => (
                  <button
                    type="button"
                    key={`book-${book.id}`}
                    className="suggestion-item"
                    onMouseDown={() => handleBookSuggestionClick(book.id, book.title)}
                  >
                    <img src={`${API_BASE}/${book.cover}`} alt={book.title} />
                    <span>{book.title}</span>
                  </button>
                ))}
              </div>
            )}

            {suggestions.authors.length > 0 && (
              <div className="suggestion-group">
                <div className="suggestion-title">Auteurs</div>
                {suggestions.authors.map((author, index) => (
                  <button
                    type="button"
                    key={`author-${author.name}-${index}`}
                    className="suggestion-item"
                    onMouseDown={() => handleAuthorSuggestionClick(author.name, author.authorId)}
                  >
                    <span>{author.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </form>

      <nav className="nav-menu">
        <button onClick={handleAboutClick} className="icon-nav-btn" aria-label="A propos" title="A propos">
          <FaInfoCircle />
        </button>
        <button onClick={handleMonetizationClick} className="icon-nav-btn" aria-label="Monetisation" title="Monetisation">
          <FaDollarSign />
        </button>
      </nav>
    </header>
  );
}

export default Header;

